// Mirrors src/utils/bunny-status.js in studx-admin-backend for BUNNY_STATUS
// and isTerminal. isAbandoned below is frontend-only -- the backend had a
// same-named function for a while (driving createBunnyUpload's resume
// decision) but it was removed: that decision turned out to need a
// status-only isResumable with no time component at all, so there is no
// backend equivalent of this function to mirror. See isResumable in the
// backend's bunny-status.js for why.
//
// These are Bunny's Get Video codes (the video-object `status` field), NOT
// the webhook body's status field. The backend's syncFromBunny is the single
// writer of bunnyStatus and it always fetches Get Video -- the webhook only
// triggers that fetch and never stores its own body.Status. So the webhook's
// documented enum (0 Queued, 1 Processing, 2 Encoding, 3 Finished, 4
// ResolutionFinished, 5 Failed, ...) is deliberately unused and must never be
// reintroduced here -- it does not match what bunnyStatus ever contains.
//
// This table also mirrors utils/bunny.js's STATUS_LABELS, which was verified
// against a live Bunny library for the dev spike page and is the reference
// both bunny-status.js files were corrected against. An earlier version of
// this file used the webhook enum by mistake -- see git history around
// 2026-08-06. The correction lands here and in the backend's
// src/utils/bunny-status.js together.
export const BUNNY_STATUS = {
  CREATED: 0,
  UPLOADED: 1,
  PROCESSING: 2,
  TRANSCODING: 3,
  FINISHED: 4,
  ERROR: 5,
  UPLOAD_FAILED: 6,
  // JIT (just-in-time) segmenting sub-states. Not user-visible: they are not
  // FINISHED and not a failure, so they fall into the same in-progress
  // bucket as PROCESSING/TRANSCODING for both the badge and isTerminal.
  JIT_SEGMENTING: 7,
  JIT_PLAYLISTS_CREATED: 8,
};

// Bunny's own names for the codes, shown verbatim in BunnyUploadPanel's
// sub-status line -- the only place raw Bunny vocabulary appears, so a
// stuck encode can be told from a slow one without opening the Bunny
// dashboard. Keys are Get Video codes -- NOT the webhook enum, where 3
// means Finished. Was duplicated verbatim inside BunnyUploadPanel.vue as a
// third copy of this table (alongside this file's own STATUS_LABELS-shaped
// data and src/utils/bunny.js's STATUS_LABELS); consolidated here since
// bunny.js belongs to the dev-only spike page and importing it would drag
// in that page's own axios client.
export const STATUS_NAMES = {
  [BUNNY_STATUS.CREATED]: 'Created',
  [BUNNY_STATUS.UPLOADED]: 'Uploaded',
  [BUNNY_STATUS.PROCESSING]: 'Processing',
  [BUNNY_STATUS.TRANSCODING]: 'Transcoding',
  [BUNNY_STATUS.FINISHED]: 'Finished',
  [BUNNY_STATUS.ERROR]: 'Error',
  [BUNNY_STATUS.UPLOAD_FAILED]: 'UploadFailed',
  [BUNNY_STATUS.JIT_SEGMENTING]: 'JitSegmenting',
  [BUNNY_STATUS.JIT_PLAYLISTS_CREATED]: 'JitPlaylistsCreated',
};

const IN_PROGRESS = [
  BUNNY_STATUS.CREATED,
  BUNNY_STATUS.UPLOADED,
  BUNNY_STATUS.PROCESSING,
  BUNNY_STATUS.TRANSCODING,
  BUNNY_STATUS.JIT_SEGMENTING,
  BUNNY_STATUS.JIT_PLAYLISTS_CREATED,
];
const FAILURES = [BUNNY_STATUS.ERROR, BUNNY_STATUS.UPLOAD_FAILED];

/**
 * `text` is an i18n key (under `vdo-set-topic.bunny-status-code.*`), not display text --
 * callers must run it through $t(). Shape otherwise unchanged.
 * @param {number|null|undefined} status
 * @return {{text: string, color: string}|null} null when there is nothing to show
 */
export function statusBadge(status) {
  if (status === null || status === undefined) return null;
  if (IN_PROGRESS.indexOf(status) !== -1) {
    return { text: 'vdo-set-topic.bunny-status-code.in-progress', color: 'warning' };
  }
  if (status === BUNNY_STATUS.FINISHED) {
    return { text: 'vdo-set-topic.bunny-status-code.finished', color: 'success' };
  }
  if (FAILURES.indexOf(status) !== -1) {
    return { text: 'vdo-set-topic.bunny-status-code.failed', color: 'danger' };
  }
  return null;
}

/**
 * No further status change expected. The topic table stops polling on true.
 * @param {number|null|undefined} status
 * @return {boolean}
 */
export function isTerminal(status) {
  return status === BUNNY_STATUS.FINISHED || FAILURES.indexOf(status) !== -1;
}

const ABANDONED_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * A topic whose Bunny video was created but never uploaded to. The tab was
 * closed before the transfer started, and Bunny is holding an empty video
 * object that nothing will ever finish. The screen offers to cancel it.
 * Frontend-only concept -- see the file header for why there is no backend
 * isAbandoned to mirror any more.
 * @param {number|null|undefined} status
 * @param {string|null|undefined} statusAt bunnyStatusAt as it arrives over
 *   GraphQL -- graphql-js serializes a Prisma DateTime through GraphQLString
 *   via Date.prototype.valueOf(), so the wire value is an all-digits
 *   epoch-milliseconds string (e.g. "1786010400000"), not ISO-8601. Accept
 *   both shapes: digits-only is parsed as a number, anything else falls back
 *   to Date.parse for callers/tests that pass an ISO string directly.
 * @param {number} [now] epoch ms to measure against, defaulting to the real
 *   clock. A Vue computed that reads Date.now() directly never recomputes,
 *   because a clock is not a reactive dependency -- so BunnyUploadPanel passes
 *   its own ticking value in and this function stops being the thing that
 *   decides when "now" is.
 * @return {boolean}
 */
export function isAbandoned(status, statusAt, now = Date.now()) {
  if (status !== null && status !== undefined) return false;
  if (!statusAt) return false;
  const started = /^\d+$/.test(statusAt) ? Number(statusAt) : new Date(statusAt).getTime();
  if (isNaN(started)) return false;
  return now - started > ABANDONED_AFTER_MS;
}
